import React, { useState } from 'react';
import SwipeableModal from '../../../components/SwipeableModal';
import Icon from '../../../components/AppIcon';

const UserGuideModal = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'Rocket',
      content: [
        {
          title: 'Welcome to MyBodyToday',
          description: 'MyBodyToday helps you track symptoms, manage health data, and share insights with healthcare providers.',
          steps: [
            'Complete your profile setup',
            'Choose your user type (Individual or Caregiver)',
            'Set up notification preferences',
            'Start your first daily check-in'
          ]
        }
      ]
    },
    {
      id: 'daily-checkin',
      title: 'Daily Check-In',
      icon: 'Calendar',
      content: [
        {
          title: 'Recording Symptoms',
          description: 'Track your daily symptoms with detailed information.',
          steps: [
            'Select symptom categories (pain, fatigue, mood, etc.)',
            'Rate severity on a 1-10 scale',
            'Choose body location using interactive mapper',
            'Add timing information (morning, afternoon, evening)',
            'Note potential triggers',
            'Track medications taken',
            'Add photos or voice notes for context'
          ]
        },
        {
          title: 'Daily Impact Assessment',
          description: 'Evaluate how symptoms affect your daily activities.',
          steps: [
            'Rate overall feeling level',
            'Assess impact on work, sleep, and activities',
            'Track mood and energy levels'
          ]
        }
      ]
    },
    {
      id: 'symptom-history',
      title: 'Symptom History',
      icon: 'History',
      content: [
        {
          title: 'Viewing Your Timeline',
          description: 'Access and analyze your symptom history.',
          steps: [
            'Switch between timeline and calendar views',
            'Filter by symptom type, severity, or date range',
            'View detailed entries with all recorded information',
            'Identify patterns and trends over time'
          ]
        },
        {
          title: 'Exporting Data',
          description: 'Share your health data with healthcare providers.',
          steps: [
            'Select date range for export',
            'Choose format (PDF or CSV)',
            'Generate doctor summary report',
            'Send via email or download'
          ]
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Insights',
      icon: 'TrendingUp',
      content: [
        {
          title: 'Advanced Analytics (Premium)',
          description: 'Gain deeper insights into your health patterns.',
          steps: [
            'View pattern recognition charts',
            'Analyze trigger correlations',
            'Track intervention effectiveness',
            'Identify seasonal correlations',
            'Get predictive insights'
          ]
        }
      ]
    },
    {
      id: 'caregiver',
      title: 'Caregiver Features',
      icon: 'Users',
      content: [
        {
          title: 'Managing Dependents',
          description: 'Care for multiple individuals from one account.',
          steps: [
            'Add dependents via email invitation or access code',
            'View all dependents on your dashboard',
            'Monitor their symptom entries and trends',
            'Receive alerts for concerning symptoms',
            'Access individual analytics for each dependent'
          ]
        },
        {
          title: 'Alert System',
          description: 'Stay informed about important health changes.',
          steps: [
            'Configure alert thresholds',
            'Receive notifications for severe symptoms',
            'View alert history and details',
            'Acknowledge and track alerts'
          ]
        }
      ]
    },
    {
      id: 'subscription',
      title: 'Subscription Plans',
      icon: 'CreditCard',
      content: [
        {
          title: 'Plan Features',
          description: 'Choose the plan that fits your needs.',
          steps: [
            'Basic (Free): 7-day history, 2 monthly exports, 50MB storage',
            '£1.99 Plan: 30-day history, 5 monthly exports, 200MB storage, 1 caregiver',
            'Basic Ultra Plan: Extended features with enhanced storage and export limits',
            'Premium (£4.99/mo): Unlimited history, advanced analytics, 5 caregivers',
            'Family (£14.99/mo): Up to 5 profiles, shared dashboard, all premium features'
          ]
        },
        {
          title: 'Managing Subscription',
          description: 'Update your plan and billing information.',
          steps: [
            'View current plan and usage limits',
            'Upgrade or downgrade plans',
            'Update payment method',
            'View billing history',
            'Cancel subscription (access until period ends)'
          ]
        },
        {
          title: 'Referral Program',
          description: 'Earn rewards by inviting friends and family.',
          steps: [
            'Share your unique referral link with others',
            'Earn rewards when referred users sign up',
            'Track your referral progress in the Referral Program section',
            'Unlock milestones and receive notifications for achievements',
            'Referral rewards are available for all plan types including Basic users'
          ]
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Security',
      icon: 'Shield',
      content: [
        {
          title: 'Data Protection',
          description: 'Your health data is secure and private.',
          steps: [
            'All sensitive data automatically encrypted with AES-256',
            'Privacy-focused design with encrypted health data storage',
            'Mandatory encryption for all symptom entries and medications',
            'Audit logs track all data access',
            'Two-factor authentication available'
          ]
        },
        {
          title: 'Data Management',
          description: 'Control your personal information.',
          steps: [
            'Export all your data anytime',
            'Delete specific entries or entire account',
            'Manage sharing permissions',
            'Review audit trail of data access'
          ]
        }
      ]
    }
  ];

  const activeContent = sections?.find(s => s?.id === activeSection);

  return (
    <SwipeableModal
      isOpen={isOpen}
      onClose={onClose}
      title="User Guide"
      maxWidth="max-w-4xl"
      showCloseButton={true}
    >
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="md:w-64 flex-shrink-0">
          <nav className="space-y-1">
            {sections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => setActiveSection(section?.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors min-h-[44px] touch-manipulation active:scale-95 ${
                  activeSection === section?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                }`}
              >
                <Icon name={section?.icon} size={20} />
                <span className="font-medium text-sm">{section?.title}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {activeContent?.content?.map((item, index) => (
              <div key={index} className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground">{item?.title}</h3>
                <p className="text-sm text-muted-foreground">{item?.description}</p>
                <ul className="space-y-2">
                  {item?.steps?.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-primary">{stepIndex + 1}</span>
                      </div>
                      <span className="text-sm text-foreground">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SwipeableModal>
  );
};

export default UserGuideModal;